import { EventEmitter } from '@visiba/std';
import { Singleton } from '@visiba/instantiation';
import { UnitsApiService } from '@src/api/units_api.service';
import { PUBLIC_SUB_URL_PATH } from '../../constants';
import { UnitModel } from '@api/generated/models';

enum EntranceTypeEnum {
	Regular = 0,
}

export interface UnitEntrance {
	id: string;
	to: string;
	internal: boolean;
	body: string;
	label: string;
	type: EntranceTypeEnum;
}

export interface UnitNodeData {
	title: string;
	body: string;
	allowClientAgents: boolean;
	hasAreaBooking: boolean;
	hasAreaDropIn: boolean;
	hasAreaMessaging: boolean;
	hasConditionWorkflow: boolean;
	hasPhysicalLocation: boolean;
	hideProvidedByVisibaCare: boolean;
	messagingIsUnlisted: boolean;
	entrances: UnitEntrance[];
}

export interface UnitNode {
	name: string;
	id: number;
	alias: string;
	route: string;
	parent: UnitNode | null;
	isListed: boolean;
	isEntrance: boolean;
	receptionId: number;
	data: UnitNodeData | null;
	children: UnitNode[];
	hasListableChildren: boolean;
	hasChildren: boolean;
}

export class UnitConfigurationError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UnitConfigurationError';
		Object.setPrototypeOf(this, UnitConfigurationError.prototype);
	}
}

export class UnitNotFoundError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UnitNotFoundError';
		Object.setPrototypeOf(this, UnitNotFoundError.prototype);
	}
}

export class UnitsNotLoadedError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UnitsNotLoadedError';
		Object.setPrototypeOf(this, UnitsNotLoadedError.prototype);
	}
}

export class UnitNotListedError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UnitNotListedError';
		Object.setPrototypeOf(this, UnitNotListedError.prototype);
	}
}

export class RouteNotAccessible extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'RouteNotAccessible';
		Object.setPrototypeOf(this, RouteNotAccessible.prototype);
	}
}

/**
 * A singleton service that manages units throughout the application.
 *
 * In the Visiba platform we have a concept called _Unit_. A Unit is a model
 * that represent a customer's department within their organization. A Unit
 * can be flagged as an _Entrance_ and if the Entrance flag is present we're
 * referring it has an _Reception_.
 *
 * Units and Receptions are different from each other, Units are part of a
 * hierarchically structure while a Reception is the place a patient can
 * "enter" (visually in the UI). They do not share the same id; meaning that a
 * Unit, as a node in the tree, is just a bridge to fetch the Reception data
 * (containing the reception id). The ids are formally known as `unitId` and
 * `receptionId` and through out our code bases we have moved more of our
 * logic to target a `receptionId` over a `unitId`. Newer code is written for
 * `receptionId` while "legacy" parts still targets `unitId`.
 */
@Singleton()
export class UnitService {
	public readonly events = new EventEmitter<{
		onTreeConstructing: null;
		onTreeConstructed: null;
	}>();
	private unitTree: Readonly<Map<number, UnitNode>> | null = null;
	private backTraceReception: Readonly<Map<string, number>> | null = null;
	private internalRootUnitId: number | null = null;

	/**
	 * Retrieves the loaded Unit Node Tree for the LicenceHolder the application is
	 * running under.
	 */
	public get units(): Readonly<Map<number, UnitNode>> {
		if (this.unitTree == null) throw new UnitsNotLoadedError('Tried to operate on unloaded units');

		return this.unitTree;
	}

	/**
	 * Retrieves the root Unit Id of the loaded Unit Node Tree for the
	 * licenceHolder the application is running under.
	 */
	public get rootUnit(): UnitNode {
		if (this.internalRootUnitId == null) throw new UnitsNotLoadedError('Tried to operate on unloaded units');

		const unit = this.getUnitNodeInTree(this.internalRootUnitId);

		return unit;
	}

	constructor(private readonly unitsApiService: UnitsApiService) {
		// Empty
	}

	/**
	 * This method will "load" the service with a collection of
	 * nodes starting from the passed root id
	 *
	 * @param rootId The root Unit node id that the remainder of Unit
	 * tree will be built on.
	 */
	public async load(rootId: number, loadedData?: UnitModel): Promise<void> {
		this.events.emit('onTreeConstructing', null);

		let model: UnitModel;
		if (loadedData != null) {
			model = loadedData;
		} else {
			const { data } = await this.unitsApiService.getTree(rootId);
			model = data;
		}
		const [unitTree, backTracedMap] = this.traverseReceptionModel(model);

		this.internalRootUnitId = rootId;

		this.backTraceReception = backTracedMap;
		this.unitTree = unitTree;

		this.events.emit('onTreeConstructed', null);
	}

	/** Given an valid reception route, the id of that unit will be resolved */
	public backTraceUnitIdFromRoute(url: string): number {
		if (this.backTraceReception == null) throw new UnitsNotLoadedError('Tried to operate on unloaded units');

		const backTraced = this.backTraceReception.get(url);
		if (backTraced == null) throw new UnitNotListedError(`The url "${url}" did not result in any matching unit`);

		return backTraced;
	}

	/** Retrieves the Unit Node for given UnitId */
	public getUnitNodeInTree(unitId: number): UnitNode {
		const unit = this.units.get(unitId);
		if (unit == null) throw new UnitNotFoundError(`No unit found with unitId ${unitId}`);

		return unit;
	}

	public getUnitNodeInTreeByReceptionId(receptionId: number): UnitNode {
		const unit = Array.from(this.units.values()).find((unit) => unit.receptionId == receptionId);
		if (unit == null) throw new UnitNotFoundError(`No unit found with receptionId ${receptionId}`);

		return unit;
	}

	public normalizePathnameToBackTraceableRoute(pathname: string): string {
		// Given the pathname: /v/1/2/3/visits?x=y&y=x
		// The goal is to normalize it to 1/2/3

		//We want to normalize the string to: v/1/2/3/visits?x=y&y=x
		function trimmedLeadingAndTrailingSlashes(str: string): string {
			return str.replace(/^\/|\/$/g, '');
		}
		let trimmedPathname = trimmedLeadingAndTrailingSlashes(pathname);

		// We want to remove the "v/" if present, giving us: 1/2/3/visits?x=y&y=x
		if (trimmedPathname.startsWith(`${PUBLIC_SUB_URL_PATH}/`)) {
			trimmedPathname = trimmedPathname.substring(2);
		}

		// We want to remove the "?x=y&y=x" if present, giving us: 1/2/3/visits
		trimmedPathname = trimmedPathname.split('?')[0];

		// We want to split at the slashes, giving us: ['1', '2', '3', 'visits?x=y&y=x']
		const chunks = trimmedPathname.split('/');
		// We want to know all the possible units that can be found in the pathname,
		// so it may be checked against in the next step
		const allRoutes: string[] = [];
		// TODO: REMOVE THIS ONCE NOT INJECTED ANY MORE
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		for (const [route] of this.backTraceReception ?? []) {
			allRoutes.push(route);
		}
		const knownUrlAliases = new Set<string>(allRoutes.join('/').split('/'));

		// We want to travel forward and check how far we can go until we find an
		// off chunk or just reached the end
		const strBuilder: string[] = [];
		while (chunks.length) {
			// It's guaranteed that chunks.length > 0 here
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const chunk = chunks.shift()!;
			if (knownUrlAliases.has(chunk)) {
				strBuilder.push(chunk);
				continue;
			}

			break;
		}

		// 1/2/3
		// Down here we're hopefully done! Just a check if the made string is valid.
		// If not, big sad :c
		const completedRoute = strBuilder.join('/');
		if (this.backTraceReception?.has(completedRoute)) {
			return completedRoute;
		}

		throw new RouteNotAccessible(
			`The route "${pathname}" couldn't be normalized into anything recognizable.
Best we could do was: "${completedRoute}", but no records of that key was found.`,
		);
	}

	private traverseReceptionModel(model: UnitModel): [Readonly<Map<number, UnitNode>>, Readonly<Map<string, number>>] {
		const extractedMapRef = new Map<number, UnitNode>();
		const backTraceReception = new Map<string, number>();

		function findListedChild(start: UnitModel[]): boolean {
			const childStack: UnitModel[] = [...start];
			while (childStack.length > 0) {
				const child = childStack.pop();
				if (child?.IsListedToClients) {
					return true;
				}

				childStack.push(...(child?.Children ?? []));
			}

			return false;
		}

		const traverseNode = (node: UnitModel): void => {
			// If it already exists the tree structure is in a state that's not
			// meant to be supported. So we exiting with an exception
			const existingNode = extractedMapRef.get(node.Id);
			if (existingNode) throw new UnitConfigurationError(`The node ${node.Id} has already been resolved in another branch`);

			// Else we need to create this node and its child nodes
			const nodeBuilder: UnitNode = {
				id: node.Id,
				// TODO PAT-426: Allow null?
				receptionId: node.ReceptionId ?? -1,
				isEntrance: node.IsClientEntrance,
				isListed: node.IsListedToClients,
				// Customers shouldn't be able to have null names, this could occur
				// though when new languages are added to the system. So we'll just
				// set this value to an empty string to avoid any type errors.
				name: node.Name ?? '',

				parent: null,
				// TODO PAT-426: Allow null?
				alias: node.UrlAlias ?? '',
				// TODO PAT-426: Allow null?
				route: node.UrlAlias ?? '',
				data: null,
				children: [],
				// TODO PAT-426: Allow null?
				hasChildren: (node.Children ?? []).length > 0,
				// TODO PAT-426: Allow null?
				hasListableChildren: findListedChild(node.Children ?? []),
			};

			const parentNode = node.ParentId ? extractedMapRef.get(node.ParentId) : undefined;
			if (parentNode) {
				nodeBuilder.parent = parentNode;

				// Recursive traverse until hitting the top, building the route for
				// this node
				// TODO PAT-426: Allow null?
				const routeList: string[] = [node.UrlAlias ?? '-'];
				const buildAliasUntilReachingTheTopNode = (node: UnitNode): void => {
					routeList.unshift(node.alias);

					if (node.parent) {
						buildAliasUntilReachingTheTopNode(node.parent);
					}
				};

				buildAliasUntilReachingTheTopNode(parentNode);
				nodeBuilder.route = routeList.join('/');

				parentNode.children.push(nodeBuilder);
			}

			// Once everything is settled, store this node for later use
			extractedMapRef.set(nodeBuilder.id, nodeBuilder);
			backTraceReception.set(nodeBuilder.route, nodeBuilder.id);

			node.Children?.forEach((childNode) => {
				traverseNode(childNode);
			});
		};

		traverseNode(model);

		return [extractedMapRef, backTraceReception];
	}
}
